<template>
  <div class="extras-mg-cal">
    <div v-if="cargando" class="extras-mg-cal__estado">Cargando…</div>

    <div v-else-if="!view.date_field" class="extras-mg-cal__estado">
      Elige arriba el campo de fecha que coloca las filas en el calendario.
    </div>

    <template v-else>
      <div class="extras-mg-cal__barra">
        <ButtonIcon icon="iconoir-nav-arrow-left" @click="moverMes(-1)" />
        <div class="extras-mg-cal__mes">{{ nombreDelMes }}</div>
        <ButtonIcon icon="iconoir-nav-arrow-right" @click="moverMes(1)" />
        <a class="extras-mg-cal__hoy" @click="irAHoy">Hoy</a>
        <span v-if="sinFecha.length" class="extras-mg-cal__sin-fecha">
          {{ sinFecha.length }} sin fecha
        </span>
        <span v-if="hayMasFilas" class="extras-mg-cal__tope">
          Se muestran las primeras {{ TOPE_TOTAL }} filas de la vista
        </span>
      </div>

      <div class="extras-mg-cal__dias-semana">
        <div v-for="d in diasSemana" :key="d" class="extras-mg-cal__dia-semana">
          {{ d }}
        </div>
      </div>

      <div class="extras-mg-cal__rejilla">
        <div
          v-for="dia in dias"
          :key="dia.iso"
          class="extras-mg-cal__dia"
          :class="{
            'extras-mg-cal__dia--fuera': !dia.delMes,
            'extras-mg-cal__dia--hoy': dia.esHoy,
          }"
        >
          <div class="extras-mg-cal__numero">{{ dia.numero }}</div>
          <div class="extras-mg-cal__tarjetas">
            <div
              v-for="fila in dia.filas"
              :key="fila.id"
              class="extras-mg-cal__tarjeta"
              @click="abrirFila(fila)"
            >
              <span
                v-if="colorDeFila(fila)"
                class="extras-mg-cal__color"
                :class="'background-color--' + colorDeFila(fila)"
              ></span>
              <span class="extras-mg-cal__hora" v-if="hora(fila)">{{
                hora(fila)
              }}</span>
              {{ titulo(fila) }}
            </div>
          </div>
        </div>
      </div>
    </template>

    <RowEditModal
      ref="rowEditModal"
      enable-navigation
      :database="database"
      :table="table"
      :view="view"
      :all-fields-in-table="fields"
      :primary-is-sortable="true"
      :visible-fields="camposDeTarjeta"
      :hidden-fields="camposOcultos"
      :rows="filas"
      :read-only="noSePuedeEditar"
      :show-hidden-fields="showHiddenFieldsInRowModal"
      @hidden="$emit('selected-row', undefined)"
      @toggle-hidden-fields-visibility="
        showHiddenFieldsInRowModal = !showHiddenFieldsInRowModal
      "
      @update="actualizarValor"
      @field-updated="$emit('refresh', $event)"
      @field-deleted="$emit('refresh')"
      @field-created="$emit('refresh')"
      @refresh-row="refrescarFila"
    ></RowEditModal>
  </div>
</template>

<script>
import ButtonIcon from '@baserow/modules/core/components/ButtonIcon'
import moment from '@baserow/modules/core/moment'
import {
  getFieldTimezone,
  getTimeMomentFormat,
} from '@baserow/modules/database/utils/date'

import vistaMG from '@extras_mg/mixins/vistaMG'

const DIAS = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom']
const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]

export default {
  name: 'CalendarioView',
  components: { ButtonIcon },
  mixins: [vistaMG],
  props: {
    database: { type: Object, required: true },
    table: { type: Object, required: true },
    view: { type: Object, required: true },
    fields: { type: Array, required: true },
    readOnly: { type: Boolean, required: true },
    storePrefix: { type: String, required: false, default: '' },
  },
  emits: ['refresh', 'selected-row'],
  data() {
    const hoy = new Date()
    return {
      rutaDeFilas: 'calendario-mg',
      diasSemana: DIAS,
      anio: hoy.getFullYear(),
      mes: hoy.getMonth(),
    }
  },
  computed: {
    campoDeFecha() {
      return this.fields.find((f) => f.id === this.view.date_field)
    },
    nombreDelMes() {
      return `${MESES[this.mes]} ${this.anio}`
    },
    porDia() {
      const mapa = {}
      const llave = `field_${this.view.date_field}`
      for (const fila of this.filas) {
        const iso = this.diaDeLaFila(fila[llave])
        if (!iso) {
          continue
        }
        ;(mapa[iso] = mapa[iso] || []).push(fila)
      }
      return mapa
    },
    sinFecha() {
      const llave = `field_${this.view.date_field}`
      return this.filas.filter((f) => !this.diaDeLaFila(f[llave]))
    },
    dias() {
      // La rejilla arranca el lunes de la semana del dia 1 y siempre son seis
      // semanas: asi el alto no salta al cambiar de mes.
      const primero = new Date(this.anio, this.mes, 1)
      const desplazamiento = (primero.getDay() + 6) % 7
      const inicio = new Date(this.anio, this.mes, 1 - desplazamiento)
      const hoyIso = this.aIso(new Date())

      const salida = []
      for (let i = 0; i < 42; i++) {
        const d = new Date(
          inicio.getFullYear(),
          inicio.getMonth(),
          inicio.getDate() + i
        )
        const iso = this.aIso(d)
        salida.push({
          iso,
          numero: d.getDate(),
          delMes: d.getMonth() === this.mes,
          esHoy: iso === hoyIso,
          filas: this.porDia[iso] || [],
        })
      }
      return salida
    },
  },
  watch: {
    'view.date_field'() {
      this.traer()
    },
  },
  methods: {
    aIso(d) {
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      return `${d.getFullYear()}-${mm}-${dd}`
    },
    /**
     * En que dia cae la fila.
     *
     * Un campo de solo fecha llega como "2026-09-05" y se usa tal cual. Uno con
     * hora llega en UTC ("2026-09-06T02:00:00Z"): cortar por la "T" lo pone en
     * el dia equivocado para cualquier evento nocturno —un show de las 21:00 en
     * Cancun (UTC-5) aparecia al dia siguiente—. Por eso se convierte a la zona
     * del campo, que es la misma que usa Baserow para pintar la celda.
     */
    diaDeLaFila(valor) {
      if (!valor || typeof valor !== 'string') {
        return null
      }
      const campo = this.campoDeFecha
      if (!campo || !campo.date_include_time) {
        const iso = valor.split('T')[0]
        return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : null
      }
      const m = moment.utc(valor, moment.ISO_8601, true)
      if (!m.isValid()) {
        return null
      }
      const zona = getFieldTimezone(campo)
      return (zona ? m.tz(zona) : m).format('YYYY-MM-DD')
    },
    /** La hora del evento, en la zona del campo. Vacio si el campo no lleva hora. */
    hora(fila) {
      const campo = this.campoDeFecha
      if (!campo || !campo.date_include_time) {
        return ''
      }
      const valor = fila[`field_${this.view.date_field}`]
      const m = moment.utc(valor, moment.ISO_8601, true)
      if (!m.isValid()) {
        return ''
      }
      const zona = getFieldTimezone(campo)
      return (zona ? m.tz(zona) : m).format(
        getTimeMomentFormat(campo.date_time_format)
      )
    },
    moverMes(delta) {
      const d = new Date(this.anio, this.mes + delta, 1)
      this.anio = d.getFullYear()
      this.mes = d.getMonth()
    },
    irAHoy() {
      const hoy = new Date()
      this.anio = hoy.getFullYear()
      this.mes = hoy.getMonth()
    },
  },
}
</script>

<style lang="scss">
/* Sin colores fijos: build-theme.py invierte cualquier hex del CSS compilado,
   asi que todo hereda con currentColor y opacidades. */
.extras-mg-cal {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.extras-mg-cal__estado {
  padding: 16px;
  font-size: 13px;
  opacity: 0.7;
}

.extras-mg-cal__barra {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
}

.extras-mg-cal__mes {
  font-weight: 600;
  min-width: 160px;
  text-align: center;
  text-transform: capitalize;
}

.extras-mg-cal__hoy {
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
}

.extras-mg-cal__sin-fecha,
.extras-mg-cal__tope {
  font-size: 12px;
  opacity: 0.6;
}

/*
 * minmax(0, 1fr) y no 1fr: el minimo de 1fr es el min-content de la columna, y
 * las tarjetas van en nowrap, asi que un titulo largo ensanchaba su dia. Las
 * columnas quedaban desiguales, se desalineaban de estos encabezados y los
 * ultimos dias de la semana se salian por la derecha con scroll horizontal.
 */
.extras-mg-cal__dias-semana {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  padding: 0 12px;
}

.extras-mg-cal__dia-semana {
  font-size: 11px;
  opacity: 0.6;
  padding: 4px 6px;
}

.extras-mg-cal__rejilla {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  grid-auto-rows: 1fr;
  gap: 1px;
  flex: 1;
  overflow: auto;
  padding: 0 12px 12px;
}

.extras-mg-cal__dia {
  min-width: 0;
  border: 1px solid;
  border-color: color-mix(in srgb, currentColor 12%, transparent);
  border-radius: 4px;
  min-height: 90px;
  padding: 4px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.extras-mg-cal__dia--fuera {
  opacity: 0.45;
}

.extras-mg-cal__dia--hoy {
  border-color: color-mix(in srgb, currentColor 45%, transparent);
}

.extras-mg-cal__numero {
  font-size: 11px;
  opacity: 0.7;
}

.extras-mg-cal__tarjetas {
  display: flex;
  flex-direction: column;
  gap: 3px;
  overflow-y: auto;
}

/*
 * flex-shrink: 0 porque con overflow: hidden el minimo de la tarjeta es 0: con
 * mas de unas diez en un dia se aplastaban a 4 px y el texto se encimaba. Asi
 * conservan su alto y la lista del dia hace scroll (overflow-y de arriba).
 */
.extras-mg-cal__tarjeta {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  line-height: 1.3;
  padding: 2px 4px;
  border-radius: 3px;
  background: color-mix(in srgb, currentColor 8%, transparent);
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.extras-mg-cal__color {
  flex: 0 0 auto;
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.extras-mg-cal__hora {
  opacity: 0.7;
}
</style>
